import { type CaptureFileJSONObject, MarkerType } from '@ikegps/core-api';

import {
	CollectionModelDrawableLine,
	CollectionModelDrawableMarker,
	CollectionModelDrawableMarkerOffset,
	isInsightOffsetDetectionClass,
} from '../model';
import { IkeUnit } from '../unit';
import type { SVGRenderOptions } from './draw/svg/svg.render';

export class IkeDrawUtil {
	static getBaseOffsetLabelText(
		capture: CaptureFileJSONObject,
		marker: CollectionModelDrawableMarker | CollectionModelDrawableMarkerOffset
	): string[] {
		const labelText = marker.label.text;

		if (marker.y > capture.resolution.height) {
			return ['↓ ' + labelText];
		}
		return labelText;
	}

	static getBaseLabelText(marker: CollectionModelDrawableMarker, options: SVGRenderOptions): string[] {
		const units = options.units;
		const labelText = marker.label.text;

		let textLines: string[] = [];
		if (labelText.length > 0) {
			textLines = textLines.concat(labelText);
		}
		const offset = marker.offset;
		if (offset && offset > 0) {
			const offsetText = IkeUnit.display(offset, units, IkeUnit.METER);
			return [`${offsetText} - ${textLines[0]}`];
		}
		return textLines;
	}

	static getMarkerLabelText(
		capture: CaptureFileJSONObject,
		marker: CollectionModelDrawableMarker,
		options: SVGRenderOptions
	): string[] {
		const units = options.units;
		const isInsightMarker = marker.insight && !marker.insight.applied;
		const isInsightMarkerLabelsVisible = options.isInsightMarkerLabelsVisible;
		const detection_class = marker.insight?.detection_class;

		if (marker.type === MarkerType.BASE) {
			return IkeDrawUtil.getBaseLabelText(marker, options);
		}

		if (marker.type === MarkerType.BASE_OFFSET) {
			return IkeDrawUtil.getBaseOffsetLabelText(capture, marker);
		}

		const label = marker.label;
		const labelLinks = label.links;
		const output: string[] = [];
		if (labelLinks.length > 0) {
			const outputText = {};
			for (const link of labelLinks) {
				link.distanceUnit = units;
				const title = link.annotationLabel;
				outputText[title] = (outputText[title] || 0) + 1;
			}

			// Squash text into a single line
			for (const textString of Object.keys(outputText)) {
				const count = outputText[textString];
				if (count < 2) {
					output.push(textString);
				} else {
					output.push(`${count}x ${textString}`);
				}
			}
		}

		for (const text of label.text) {
			output.push(text);
		}

		if (
			isInsightMarker &&
			isInsightMarkerLabelsVisible &&
			detection_class &&
			!isInsightOffsetDetectionClass(detection_class || '')
		) {
			output.push(detection_class);
		}

		// The test (output.length == 2) is only valid if one of them is the marker value.
		// This assumption is not guaranteed anymore with the detection class.
		// This part of the code might eventually require a refactor, if more if statements are added in the future.
		if (marker.value) {
			output.push(IkeUnit.display(marker.value, units, IkeUnit.METER));

			if (output.length === 2) {
				return [`${output[1]} - ${output[0]}`];
			}
		}

		return output;
	}

	static getLineLabelText(line: CollectionModelDrawableLine, options: SVGRenderOptions): string[] {
		const units = options.units;
		const output = line.label.text.slice();
		output.push(IkeUnit.display(line.value, units, IkeUnit.METER));

		if (output.length === 2) {
			return [`${output[1]} - ${output[0]}`];
		}

		return output;
	}
}
